import { useEffect, useState } from 'react';
import type { CellContext, CellEditorProps } from '../../domain';

export function editorLabel<T>(ctx: CellContext<T>): string {
    return `Edit ${ctx.column.header ?? ctx.column.id ?? 'cell'}`;
}

export interface InputEditorProps<T> extends CellEditorProps<T> {
    type: 'number' | 'date';
}

function InputEditor<T>(props: InputEditorProps<T>): JSX.Element {
    const { value, ctx, type, onCommit, onCancel } = props;
    const committed = toInputValue(value, type);
    const [draft, setDraft] = useState(committed);

    useEffect(() => {
        setDraft(committed);
    }, [committed]);

    const commit = (): void => {
        if (draft === committed) return;
        onCommit(fromInputValue(draft, type));
    };
    const cancel = (): void => {
        setDraft(committed);
        onCancel();
    };

    return (
        <input
            type={type}
            value={draft}
            aria-label={editorLabel(ctx)}
            className="ics-table__cell-editor"
            onChange={(e) => {
                setDraft(e.target.value);
            }}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') cancel();
            }}
        />
    );
}

function toInputValue(value: unknown, type: 'number' | 'date'): string {
    if (value === null || value === undefined || value === '') return '';
    if (type === 'number') return String(value);
    const date =
        value instanceof Date ? value : new Date(value as string | number);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

function fromInputValue(draft: string, type: 'number' | 'date'): unknown {
    if (draft === '') return null;
    return type === 'number' ? Number(draft) : new Date(draft);
}

export function NumberEditor<T>(props: CellEditorProps<T>): JSX.Element {
    return <InputEditor {...props} type="number" />;
}

export function DateEditor<T>(props: CellEditorProps<T>): JSX.Element {
    return <InputEditor {...props} type="date" />;
}
